import { createRef, useCallback, useRef, useState, type RefObject } from 'react';
import type { EntityPickerBodyHandle } from '@bliks-io/ui';

const EMPTY_IDS: string[] = [];

/**
 * One stable EntityPickerBody ref per type — sharing a single ref races with
 * React's useImperativeHandle cleanup when the exiting picker unmounts during
 * the phase crossfade and would null out the entering picker's handle.
 */
export function useBodyRefsByType<K extends string>(
  keys: readonly K[]
): Record<K, RefObject<EntityPickerBodyHandle | null>> {
  const refsRef = useRef<Record<K, RefObject<EntityPickerBodyHandle | null>> | null>(null);
  refsRef.current ??= Object.fromEntries(
    keys.map((key) => [key, createRef<EntityPickerBodyHandle | null>()])
  ) as Record<K, RefObject<EntityPickerBodyHandle | null>>;
  return refsRef.current;
}

/** Per-type draft selection, so switching types in the nav keeps prior picks. */
export function useTentativeByType<K extends string>() {
  const [tentativeByType, setTentativeByType] = useState<Partial<Record<K, string[]>>>({});

  const selectedIdsFor = useCallback(
    (type: K): string[] => tentativeByType[type] ?? EMPTY_IDS,
    [tentativeByType]
  );

  const updateTentative = useCallback((type: K, ids: string[]) => {
    setTentativeByType((prev) => ({ ...prev, [type]: ids }));
  }, []);

  const resetTentative = useCallback((next: Partial<Record<K, string[]>> = {}) => {
    setTentativeByType(next);
  }, []);

  return { tentativeByType, selectedIdsFor, updateTentative, resetTentative };
}
